import * as React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Alert,
  Snackbar,
  Typography,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Devices as DevicesIcon,
  Delete as DeleteIcon,
  VerifiedUser as VerifiedUserIcon,
  PhoneAndroid as PhoneIcon,
  Computer as ComputerIcon,
} from '@mui/icons-material';
import api from '../services/api';

export default function SecuritySettings() {
  // Estados dispositivos
  const [devices, setDevices] = React.useState([]);
  const [loadingDevices, setLoadingDevices] = React.useState(true);

  // Dialogos
  const [removeDeviceDialog, setRemoveDeviceDialog] = React.useState(null);

  // Loading states
  const [isRemovingDevice, setIsRemovingDevice] = React.useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Carrega dispositivos
  const loadDevices = async () => {
    setLoadingDevices(true);
    try {
      const response = await api.get('/devices/');
      setDevices(response.data.devices || []);
    } catch (err) {
      console.error('Erro ao carregar dispositivos:', err);
    } finally {
      setLoadingDevices(false);
    }
  };

  React.useEffect(() => {
    loadDevices();
  }, []);

  // Handlers dispositivos
  const handleRemoveDevice = async (deviceId) => {
    setIsRemovingDevice(true);
    try {
      await api.delete(`/devices/${deviceId}/`);
      setDevices(devices.filter((d) => d.id !== deviceId));
      setRemoveDeviceDialog(null);
      setSnackbar({
        open: true,
        message: 'Dispositivo removido.',
        severity: 'success',
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.detail || 'Erro ao remover dispositivo.',
        severity: 'error',
      });
    } finally {
      setIsRemovingDevice(false);
    }
  };

  const handleRemoveAllDevices = async () => {
    setIsRemovingDevice(true);
    try {
      await api.delete('/devices/all/');
      setDevices([]);
      setSnackbar({
        open: true,
        message: 'Todos os dispositivos foram removidos.',
        severity: 'success',
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.detail || 'Erro ao remover dispositivos.',
        severity: 'error',
      });
    } finally {
      setIsRemovingDevice(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getDeviceIcon = (deviceName) => {
    const name = deviceName.toLowerCase();
    if (name.includes('mobile') || name.includes('android') || name.includes('iphone')) {
      return <PhoneIcon />;
    }
    return <ComputerIcon />;
  };

  return (
    <Box sx={{ p: 3, maxWidth: 800 }}>
      <Typography variant="h5" gutterBottom>
        Configuracoes de Seguranca
      </Typography>

      {/* Card 2FA - Sempre ativo */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          avatar={<SecurityIcon color="primary" />}
          title="Autenticacao em Duas Etapas (2FA)"
          subheader="Protecao extra para sua conta"
        />
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="body1" sx={{ mr: 2 }}>
              Status:
            </Typography>
            <Chip
              icon={<VerifiedUserIcon />}
              label="Sempre Ativo"
              color="success"
              variant="filled"
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            A verificacao em duas etapas e obrigatoria para todos os usuarios.
            Voce recebera um codigo por email ao fazer login em um dispositivo nao confiavel.
          </Typography>
        </CardContent>
      </Card>

      {/* Card Dispositivos Confiaveis */}
      <Card>
        <CardHeader
          avatar={<DevicesIcon color="primary" />}
          title="Dispositivos Confiaveis"
          subheader="Dispositivos que nao precisam de verificacao 2FA"
          action={
            devices.length > 0 && (
              <Button
                color="error"
                size="small"
                onClick={handleRemoveAllDevices}
                disabled={isRemovingDevice}
              >
                Remover Todos
              </Button>
            )
          }
        />
        <CardContent>
          {loadingDevices ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : devices.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Nenhum dispositivo confiavel cadastrado.
            </Typography>
          ) : (
            <List>
              {devices.map((device, index) => (
                <React.Fragment key={device.id}>
                  {index > 0 && <Divider />}
                  <ListItem>
                    <ListItemIcon>{getDeviceIcon(device.device_name)}</ListItemIcon>
                    <ListItemText
                      primary={device.device_name}
                      secondary={
                        <>
                          <Typography variant="caption" display="block">
                            Adicionado: {formatDate(device.trusted_at)}
                          </Typography>
                          <Typography variant="caption" display="block">
                            Ultimo uso: {formatDate(device.last_used)}
                          </Typography>
                          <Typography variant="caption" display="block">
                            IP: {device.ip_on_trust}
                          </Typography>
                        </>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        color="error"
                        onClick={() => setRemoveDeviceDialog(device)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Dialog Remover Dispositivo */}
      <Dialog
        open={!!removeDeviceDialog}
        onClose={() => setRemoveDeviceDialog(null)}
      >
        <DialogTitle>Remover Dispositivo</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja remover "{removeDeviceDialog?.device_name}"
            dos dispositivos confiaveis?
            <br />
            <br />
            Na proxima vez que fizer login neste dispositivo, sera necessario
            verificar com codigo 2FA.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRemoveDeviceDialog(null)}
            disabled={isRemovingDevice}
          >
            Cancelar
          </Button>
          <Button
            onClick={() => handleRemoveDevice(removeDeviceDialog?.id)}
            variant="contained"
            color="error"
            disabled={isRemovingDevice}
          >
            {isRemovingDevice ? <CircularProgress size={20} /> : 'Remover'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
